import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import { referralLabel, REFERRAL_STATUS_OPTIONS } from '../utils/referralStatus';
import ListPager from '../components/ListPager';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const DOCTOR_CABINET_URL = process.env.REACT_APP_DOCTOR_CABINET_URL || 'http://127.0.0.1:3001';

const MONTHS = ['', 'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const STATUS_OPTIONS = REFERRAL_STATUS_OPTIONS;
const PROFILE_FIELD_LABELS = {
  last_name: 'Фамилия', first_name: 'Имя', middle_name: 'Отчество', birth_date: 'Дата рождения',
  phone: 'Телефон', specialty: 'Специальность', tax_status: 'Налоговый статус',
  inn: 'ИНН', bank_name: 'Банк', bank_bik: 'БИК банка', bank_account: 'Расчётный счёт',
  bank_corr_account: 'Корр. счёт', ogrnip: 'ОГРНИП',
};
const PAYOUT_FIELDS = [
  ['inn', 'ИНН'], ['bank_name', 'Банк'], ['bank_bik', 'БИК банка'],
  ['bank_account', 'Расчётный счёт'], ['bank_corr_account', 'Корр. счёт'], ['ogrnip', 'ОГРНИП'],
];

function monthOptions() {
  const opts = [{ value: '', label: 'Все месяцы' }];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleString('ru', { month: 'long', year: 'numeric' });
    opts.push({ value: `${d.getFullYear()}-${d.getMonth() + 1}`, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
}
const MONTH_OPTIONS = monthOptions();
const REF_LIMIT = 20;
const COM_LIMIT = 50;

export default function DoctorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [showClearBase, setShowClearBase] = useState(false);
  const [clearBaseMsg, setClearBaseMsg] = useState('');
  const [showSuspend, setShowSuspend] = useState(false);
  const [suspendConfirmName, setSuspendConfirmName] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendMsg, setSuspendMsg] = useState('');
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [deactivateConfirmName, setDeactivateConfirmName] = useState('');
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivateMsg, setDeactivateMsg] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [verifyMsg, setVerifyMsg] = useState('');
  const [showPayout, setShowPayout] = useState(false);
  const [showProfileReject, setShowProfileReject] = useState(false);
  const [profileRejectReason, setProfileRejectReason] = useState('');
  const [profileMsg, setProfileMsg] = useState('');

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };
  const [impersonateBusy, setImpersonateBusy] = useState(false);

  const impersonate = async () => {
    setImpersonateBusy(true);
    try {
      const r = await axios.post(`${API}/admin/doctors/${id}/impersonate`, {}, { headers });
      window.open(`${DOCTOR_CABINET_URL}/admin-session?token=${encodeURIComponent(r.data.token)}`, '_blank');
    } catch { /* тихо — если не получилось, кнопку можно нажать снова */ }
    setImpersonateBusy(false);
  };

  const savePwd = async () => {
    if (!pwd || pwd.length < 6) { setPwdMsg('Пароль минимум 6 символов'); return; }
    setBusy(true); setPwdMsg('');
    try {
      const r = await axios.post(`${API}/admin/doctors/${id}/password`, { password: pwd }, { headers });
      setPwdMsg(r.data.message); setShowPwd(false); setPwd('');
    } catch (e) { setPwdMsg(e?.response?.data?.detail || 'Ошибка'); } finally { setBusy(false); }
  };

  const resetNonArrived = async () => {
    setBusy(true); setClearBaseMsg('');
    try {
      const r = await axios.post(`${API}/admin/doctors/${id}/reset-non-arrived`, {}, { headers });
      setClearBaseMsg(r.data.message || 'Готово'); setShowClearBase(false); load(dateFrom, dateTo);
    } catch (e) { setClearBaseMsg(e?.response?.data?.detail || 'Не удалось сбросить счётчик'); }
    finally { setBusy(false); }
  };

  const doSuspend = async () => {
    setBusy(true); setSuspendMsg('');
    try {
      await axios.post(`${API}/admin/doctors/${id}/suspend`, null,
        { headers, params: { confirm_name: suspendConfirmName, reason: suspendReason || undefined } });
      setShowSuspend(false); setSuspendConfirmName(''); setSuspendReason(''); load(dateFrom, dateTo);
    } catch (e) { setSuspendMsg(e?.response?.data?.detail || 'Не удалось заблокировать кабинет'); }
    finally { setBusy(false); }
  };

  const doUnsuspend = async () => {
    setBusy(true);
    try { await axios.post(`${API}/admin/doctors/${id}/unsuspend`, {}, { headers }); load(dateFrom, dateTo); }
    finally { setBusy(false); }
  };

  const doDeactivate = async () => {
    setBusy(true); setDeactivateMsg('');
    try {
      await axios.post(`${API}/admin/doctors/${id}/deactivate`, null,
        { headers, params: { confirm_name: deactivateConfirmName, reason: deactivateReason || undefined } });
      setShowDeactivate(false); setDeactivateConfirmName(''); setDeactivateReason(''); load(dateFrom, dateTo);
    } catch (e) { setDeactivateMsg(e?.response?.data?.detail || 'Не удалось отключить аккаунт'); }
    finally { setBusy(false); }
  };

  const doReactivate = async () => {
    setBusy(true);
    try { await axios.post(`${API}/admin/doctors/${id}/reactivate`, {}, { headers }); load(dateFrom, dateTo); }
    finally { setBusy(false); }
  };

  const downloadVerificationDoc = async () => {
    try {
      const r = await axios.get(`${API}/admin/doctors/${id}/verification-document`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch { setVerifyMsg('Не удалось открыть документ'); }
  };
  const approveVerification = async () => {
    setBusy(true); setVerifyMsg('');
    try { await axios.post(`${API}/admin/doctors/${id}/verify-approve`, {}, { headers }); load(dateFrom, dateTo); }
    catch (e) { setVerifyMsg(e?.response?.data?.detail || 'Не удалось подтвердить'); }
    finally { setBusy(false); }
  };
  const rejectVerification = async () => {
    if (!rejectReason.trim()) { setVerifyMsg('Укажите причину отказа'); return; }
    setBusy(true); setVerifyMsg('');
    try {
      await axios.post(`${API}/admin/doctors/${id}/verify-reject`, {}, { headers, params: { reason: rejectReason } });
      setShowReject(false); setRejectReason(''); load(dateFrom, dateTo);
    } catch (e) { setVerifyMsg(e?.response?.data?.detail || 'Не удалось отклонить'); }
    finally { setBusy(false); }
  };

  const approveProfileRequest = async (reqId) => {
    setBusy(true); setProfileMsg('');
    try { await axios.post(`${API}/admin/profile-requests/${reqId}/approve`, {}, { headers }); load(dateFrom, dateTo); }
    catch (e) { setProfileMsg(e?.response?.data?.detail || 'Не удалось принять заявку'); }
    finally { setBusy(false); }
  };
  const rejectProfileRequest = async (reqId) => {
    if (!profileRejectReason.trim()) { setProfileMsg('Укажите причину отказа'); return; }
    setBusy(true); setProfileMsg('');
    try {
      await axios.post(`${API}/admin/profile-requests/${reqId}/reject`, {}, { headers, params: { reason: profileRejectReason } });
      setShowProfileReject(false); setProfileRejectReason(''); load(dateFrom, dateTo);
    } catch (e) { setProfileMsg(e?.response?.data?.detail || 'Не удалось отклонить заявку'); }
    finally { setBusy(false); }
  };

  // ── Обзор ──
  const load = useCallback((from, to) => {
    const params = {};
    if (from && to) { params.date_from = from; params.date_to = to; }
    axios.get(`${API}/admin/doctors/${id}`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setData(r.data); setError(''); })
      .catch(() => setError('Не удалось загрузить врача'));
  }, [id, key]);
  useEffect(() => { load(); }, [load]);
  const apply = () => load(dateFrom, dateTo);
  const reset = () => { setDateFrom(''); setDateTo(''); load('', ''); };

  // ── Вкладка «Направления» ──
  const [refItems, setRefItems] = useState([]);
  const [refTotal, setRefTotal] = useState(0);
  const [refOffset, setRefOffset] = useState(0);
  const [refSearch, setRefSearch] = useState('');
  const [refStatus, setRefStatus] = useState('');
  const [refSort, setRefSort] = useState('bound_at');
  const [refOrder, setRefOrder] = useState('desc');
  const fetchRefs = useCallback(() => {
    const params = { search: refSearch || undefined, status: refStatus || undefined, sort: refSort, order: refOrder, limit: REF_LIMIT, offset: refOffset };
    axios.get(`${API}/admin/doctors/${id}/referrals`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setRefItems(r.data.items); setRefTotal(r.data.total); });
  }, [id, key, refSearch, refStatus, refSort, refOrder, refOffset]);
  useEffect(() => { if (tab !== 'referrals') return; const t = setTimeout(fetchRefs, 250); return () => clearTimeout(t); }, [tab, fetchRefs]);
  const refToggleSort = (f) => { setRefOffset(0); if (refSort === f) setRefOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setRefSort(f); setRefOrder('asc'); } };
  const refArrow = (f) => (refSort !== f ? '' : refOrder === 'asc' ? ' ▲' : ' ▼');

  // ── Вкладка «Комиссии» ──
  const [comItems, setComItems] = useState([]);
  const [comTotal, setComTotal] = useState(0);
  const [comOffset, setComOffset] = useState(0);
  const [comSearch, setComSearch] = useState('');
  const [comMonth, setComMonth] = useState('');
  const [comSort, setComSort] = useState('period');
  const [comOrder, setComOrder] = useState('desc');
  const fetchComs = useCallback(() => {
    const params = { search: comSearch || undefined, sort: comSort, order: comOrder, limit: COM_LIMIT, offset: comOffset };
    if (comMonth) { const [y, m] = comMonth.split('-'); params.year = y; params.month = m; }
    axios.get(`${API}/admin/doctors/${id}/commissions`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setComItems(r.data.items); setComTotal(r.data.total); });
  }, [id, key, comSearch, comMonth, comSort, comOrder, comOffset]);
  useEffect(() => { if (tab !== 'commissions') return; const t = setTimeout(fetchComs, 250); return () => clearTimeout(t); }, [tab, fetchComs]);
  const comToggleSort = (f) => { setComOffset(0); if (comSort === f) setComOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setComSort(f); setComOrder('asc'); } };
  const comArrow = (f) => (comSort !== f ? '' : comOrder === 'asc' ? ' ▲' : ' ▼');

  // ── Вкладка «Операции» (N-118) — закрытые направления по программе «Операции» этого
  // врача, с финансовыми данными (по аналогии с общей страницей «Комиссии операций»,
  // но отфильтровано по врачу; открытые/ещё не закрытые окна тут не видны — они уже
  // есть в общей вкладке «Направления» этой же страницы).
  const [opItems, setOpItems] = useState([]);
  const [opTotal, setOpTotal] = useState(0);
  const [opOffset, setOpOffset] = useState(0);
  const [opSort, setOpSort] = useState('invoiced_at');
  const [opOrder, setOpOrder] = useState('desc');
  const fetchOps = useCallback(() => {
    const params = { doctor_id: id, sort: opSort, order: opOrder, limit: COM_LIMIT, offset: opOffset };
    axios.get(`${API}/admin/operation-invoices`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setOpItems(r.data.items); setOpTotal(r.data.total); });
  }, [id, key, opSort, opOrder, opOffset]);
  useEffect(() => { if (tab !== 'operations') return; const t = setTimeout(fetchOps, 250); return () => clearTimeout(t); }, [tab, fetchOps]);
  const opToggleSort = (f) => { setOpOffset(0); if (opSort === f) setOpOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setOpSort(f); setOpOrder('asc'); } };
  const opArrow = (f) => (opSort !== f ? '' : opOrder === 'asc' ? ' ▲' : ' ▼');

  if (error) return <Layout><div style={styles.empty}>{error}</div></Layout>;
  if (!data) return <Layout><div style={styles.empty}>Загрузка…</div></Layout>;

  const TabBtn = ({ id: tid, label }) => (
    <div style={{ ...styles.tab, ...(tab === tid ? styles.tabActive : {}) }} onClick={() => setTab(tid)}>{label}</div>
  );

  return (
    <Layout>
      <div style={styles.back} onClick={() => navigate('/doctors')}>← К списку врачей</div>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{data.name}</h1>
          <div style={styles.sub}>{data.specialty || 'специальность не указана'}{data.public_id ? ` · ${data.public_id}` : ''}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <StatusBadge data={data} />
            <StatusInfo def="doctor_active" />
            <VerificationBadge status={data.verification_status} />
            <StatusInfo def="doctor_verification" />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {pwdMsg && <span style={styles.actionMsg}>{pwdMsg}</span>}
            <button style={styles.btn} onClick={impersonate} disabled={impersonateBusy} title="Открыть ЛК врача в новой вкладке от его лица (для разбора обращений в ТП)">
              {impersonateBusy ? 'Открываем…' : 'Войти в кабинет'}
            </button>
            <button style={styles.btnGhost} onClick={() => { setPwd(''); setPwdMsg(''); setShowPwd(true); }}>Сменить пароль</button>
          </div>
        </div>
      </div>

      <div style={styles.tabs}>
        <TabBtn id="overview" label="Обзор" />
        <TabBtn id="referrals" label="Направления" />
        <TabBtn id="commissions" label="Комиссии по клиникам и месяцам" />
        <TabBtn id="operations" label="Операции" />
      </div>

      {/* ── ОБЗОР ── */}
      {tab === 'overview' && (
        <>
          <div style={styles.card}>
            <div style={styles.grid}>
              <Field label="ID врача" value={data.public_id || '—'} />
              <Field label="Email" value={data.email} />
              <Field label="Телефон" value={data.phone || '—'} />
              <Field label="Дата рождения" value={data.birth_date ? new Date(data.birth_date).toLocaleDateString('ru') : '—'} />
              <Field label="Налоговый статус" value={data.tax_status === 'self_employed' ? 'Самозанятый' : data.tax_status === 'individual_entrepreneur' ? 'ИП' : '—'} />
              <div>
                <div style={styles.fieldLabel}>Реквизиты</div>
                <button style={styles.btnGhost} onClick={() => setShowPayout(true)}>
                  {data.has_payout_details ? 'Показать реквизиты' : 'Не указаны'}
                </button>
              </div>
              <Field label="Регистрация" value={new Date(data.created_at).toLocaleDateString('ru')} />
            </div>
          </div>

          <div style={styles.filterBar}>
            <button style={styles.btnGhost} onClick={reset}>Всё время</button>
            <input type="date" style={styles.dateInput} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            <span style={{ color: '#64748b' }}>—</span>
            <input type="date" style={styles.dateInput} value={dateTo} onChange={e => setDateTo(e.target.value)} />
            <button style={styles.btn} onClick={apply} disabled={!dateFrom || !dateTo}>Применить</button>
          </div>

          <div style={styles.metrics}>
            <Metric label="Направлений" value={data.referrals_count} />
            <Metric label="Заработано врачом" value={`${fmtMoney(data.total_earned)} ₽`} color="#2EC4B6" />
            <Metric label="Доход Refero" value={`${fmtMoney(data.total_platform_fee)} ₽`} />
          </div>
          {data.limits && (
            <div style={styles.limitsCard}>
              <div style={styles.limitsTitle}>Лимиты добавления пациентов (анти-фрод)</div>
              <LimitRow label={<>Активная база направлений<StatusInfo def="doctor_active_base" /></>}
                used={data.limits.base_used} max={data.limits.base_limit} />
              <LimitRow label={<>База недошедших направлений<StatusInfo def="doctor_non_arrived" /></>}
                used={data.limits.non_arrived_used} max={data.limits.non_arrived_limit} />
              <LimitRow label={<>Добавлено пациентов сегодня<StatusInfo def="doctor_daily_add" /></>}
                used={data.limits.daily_used} max={data.limits.daily_limit} />
              {data.is_blocked && (
                <div style={styles.blockBanner}>
                  <div><b>Заблокирован.</b> {data.block_reason || ''}{data.blocked_at ? ` · ${new Date(data.blocked_at).toLocaleString('ru')}` : ''}</div>
                  <button style={styles.unblockBtn} onClick={() => { setClearBaseMsg(''); setShowClearBase(true); }} disabled={busy}>Сбросить счётчик и разблокировать</button>
                </div>
              )}
              {clearBaseMsg && <div style={styles.actionMsg}>{clearBaseMsg}</div>}
            </div>
          )}

          <div style={styles.h2row}><h2 style={styles.h2}>Верификация ЕГИСЗ</h2></div>
          <div style={styles.card}>
            <div style={styles.grid}>
              <Field label="Статус" value={<VerificationBadge status={data.verification_status} />} />
              <Field label="Документ загружен" value={data.verification_doc_uploaded_at ? new Date(data.verification_doc_uploaded_at).toLocaleString('ru') : '—'} />
              <Field label="Проверен" value={data.verification_reviewed_at ? new Date(data.verification_reviewed_at).toLocaleString('ru') : '—'} />
            </div>
            {data.verification_status === 'rejected' && data.verification_reject_reason && (
              <div style={{ ...styles.warnNote, marginTop: 12 }}>Причина отказа: {data.verification_reject_reason}</div>
            )}
            {verifyMsg && <div style={styles.errText}>{verifyMsg}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {data.has_verification_doc && (
                <button style={styles.btnGhost} onClick={downloadVerificationDoc}>Открыть документ</button>
              )}
              {data.verification_status === 'pending' && (
                <>
                  <button style={styles.btn} onClick={approveVerification} disabled={busy}>Принять</button>
                  <button style={styles.btnDanger} onClick={() => { setVerifyMsg(''); setRejectReason(''); setShowReject(true); }} disabled={busy}>Отклонить</button>
                </>
              )}
            </div>
          </div>

          {data.pending_profile_request && (
            <>
              <div style={styles.h2row}><h2 style={styles.h2}>Заявка на изменение профиля</h2></div>
              <div style={styles.card}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {Object.entries(data.pending_profile_request.changes).map(([field, diff]) => (
                    <div key={field} style={{ fontSize: 13, color: '#e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>{PROFILE_FIELD_LABELS[field] || field}: </span>
                      <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{diff.old || '—'}</span>
                      {' → '}
                      <span style={{ fontWeight: 600 }}>{diff.new || '—'}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                  Отправлена {new Date(data.pending_profile_request.requested_at).toLocaleString('ru')}
                </div>
                {profileMsg && <div style={styles.errText}>{profileMsg}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={styles.btn} onClick={() => approveProfileRequest(data.pending_profile_request.id)} disabled={busy}>Принять</button>
                  <button style={styles.btnDanger} onClick={() => { setProfileMsg(''); setProfileRejectReason(''); setShowProfileReject(true); }} disabled={busy}>Отклонить</button>
                </div>
              </div>
            </>
          )}

          <div style={styles.dangerZone}>
            {!data.is_active ? (
              <div style={styles.blockBanner}>
                <div><b>Аккаунт отключён администратором.</b> {data.deactivate_reason || ''}{data.deactivated_at ? ` · ${new Date(data.deactivated_at).toLocaleString('ru')}` : ''}</div>
                <button style={styles.unblockBtn} onClick={doReactivate} disabled={busy}>Активировать аккаунт</button>
              </div>
            ) : data.is_suspended ? (
              <div style={styles.blockBanner}>
                <div><b>Кабинет заблокирован администратором.</b> {data.suspend_reason || ''}{data.suspended_at ? ` · ${new Date(data.suspended_at).toLocaleString('ru')}` : ''}</div>
                <button style={styles.unblockBtn} onClick={doUnsuspend} disabled={busy}>Разблокировать кабинет</button>
              </div>
            ) : (
              <>
                <button style={styles.btnDanger} onClick={() => { setSuspendMsg(''); setShowSuspend(true); }} disabled={busy}>Заблокировать кабинет врача</button>
                <button style={styles.btnDanger} onClick={() => { setDeactivateMsg(''); setShowDeactivate(true); }} disabled={busy}>Отключить врача</button>
              </>
            )}
          </div>
        </>
      )}

      {/* ── НАПРАВЛЕНИЯ ── */}
      {tab === 'referrals' && (
        <>
          <div style={styles.filterBar}>
            <input style={styles.searchInput} placeholder="Поиск по пациенту / клинике / номеру направления" value={refSearch}
              onChange={e => { setRefSearch(e.target.value); setRefOffset(0); }} />
            <select style={styles.select} value={refStatus} onChange={e => { setRefStatus(e.target.value); setRefOffset(0); }}>
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: refCols }}>
              <div style={styles.th}>ID</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('patient_name')}>Пациент{refArrow('patient_name')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('target_name')}>Клиника / сеть{refArrow('target_name')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('status')}>Статус{refArrow('status')}<StatusInfo def="referral" /></div>
              <div style={styles.th}>Дата $ заявлено<StatusInfo def="duplicate_first_payment" /></div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('bound_at')}>Привязан{refArrow('bound_at')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('primary_visit_at')}>Первичный визит{refArrow('primary_visit_at')}</div>
            </div>
            {refItems.map(r => (
              <div key={r.id} style={{ ...styles.row, gridTemplateColumns: refCols }}>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>REF-{r.id}</div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/patients/${r.patient_id}`)}>{r.patient_name}</div>
                <div style={{ ...styles.td, ...(r.target_id ? styles.link : {}) }} onClick={() => r.target_id && navigate(r.target_type === 'network' ? `/networks/${r.target_id}` : `/clinics/${r.target_id}`)}>{r.target_name}</div>
                <div style={styles.td}>{referralLabel(r.status, r.clinic_confirmed_at)}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{r.status === 'duplicate' && r.duplicate_first_payment_date ? new Date(r.duplicate_first_payment_date).toLocaleDateString('ru') : '—'}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{new Date(r.bound_at).toLocaleDateString('ru')}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{r.primary_visit_at ? new Date(r.primary_visit_at).toLocaleDateString('ru') : '—'}</div>
              </div>
            ))}
            {refItems.length === 0 && <div style={styles.empty}>Направлений не найдено</div>}
          </div>
          <ListPager total={refTotal} limit={REF_LIMIT} offset={refOffset} onPage={setRefOffset} />
        </>
      )}

      {/* ── КОМИССИИ ── */}
      {tab === 'commissions' && (
        <>
          <div style={styles.filterBar}>
            <input style={styles.searchInput} placeholder="Поиск по клинике / сети" value={comSearch}
              onChange={e => { setComSearch(e.target.value); setComOffset(0); }} />
            <select style={styles.select} value={comMonth} onChange={e => { setComMonth(e.target.value); setComOffset(0); }}>
              {MONTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: comCols }}>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => comToggleSort('period')}>Период{comArrow('period')}</div>
              <div style={styles.th}>Клиника / сеть</div>
              <div style={styles.th}>Первичные</div>
              <div style={styles.th}>Повторные</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => comToggleSort('subtotal')}>Итого{comArrow('subtotal')}</div>
              <div style={styles.th}>Refero</div>
              <div style={styles.th}>Квота</div>
              <div style={styles.th}>Выплата<StatusInfo def="payment" /></div>
            </div>
            {comItems.map((c, i) => (
              <div key={i} style={{ ...styles.row, gridTemplateColumns: comCols }}>
                <div style={styles.td}>{MONTHS[c.period_month]} {c.period_year}</div>
                <div style={{ ...styles.td, ...(c.target_id ? styles.link : {}) }} onClick={() => c.target_id && navigate(c.target_type === 'network' ? `/networks/${c.target_id}` : `/clinics/${c.target_id}`)}>{c.target_name}</div>
                <div style={styles.td}>{fmtMoney(c.primary_amount)} ₽</div>
                <div style={styles.td}>{fmtMoney(c.repeat_amount)} ₽</div>
                <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 600 }}>{fmtMoney(c.subtotal)} ₽</div>
                <div style={styles.td}>{fmtMoney(c.platform_fee)} ₽</div>
                <div style={styles.td}>
                  {c.quota_fulfilled
                    ? <span style={styles.badgeGreen}>✅ {c.quota_count}{c.quota_min != null ? `/${c.quota_min}` : ''}</span>
                    : <span style={styles.badgeGray}>⏳ {c.quota_count}{c.quota_min != null ? `/${c.quota_min}` : ''}</span>}
                </div>
                <div style={styles.td}>{c.is_paid ? <span style={styles.badgeGreen}>Выплачено</span> : <span style={styles.badgeGray}>Ожидает</span>}</div>
              </div>
            ))}
            {comItems.length === 0 && <div style={styles.empty}>Комиссий не найдено</div>}
          </div>
          <ListPager total={comTotal} limit={COM_LIMIT} offset={comOffset} onPage={setComOffset} />
        </>
      )}

      {/* ── ОПЕРАЦИИ (N-118) ── */}
      {tab === 'operations' && (
        <>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: opCols }}>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('target_name')}>Клиника / сеть{opArrow('target_name')}</div>
              <div style={styles.th}>Номер направления</div>
              <div style={styles.th}>Период</div>
              <div style={styles.th}>Сумма оплат</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('total_amount')}>Врачу{opArrow('total_amount')}</div>
              <div style={styles.th}>Refero</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('status')}>Счёт{opArrow('status')}<StatusInfo def="operation_invoice" /></div>
            </div>
            {opItems.map(row => (
              <div key={row.referral_id} style={{ ...styles.row, gridTemplateColumns: opCols }}>
                <div style={{ ...styles.td, ...(row.target_id ? styles.link : {}) }} onClick={() => row.target_id && navigate(row.target_type === 'network' ? `/networks/${row.target_id}` : `/clinics/${row.target_id}`)}>
                  {row.target_name}
                </div>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>REF-{row.referral_id}</div>
                <div style={{ ...styles.td, color: '#94a3b8', fontSize: 12 }}>{row.window_start} – {row.window_end}</div>
                <div style={styles.td}>{fmtMoney(row.total_amount)} ₽ <span style={{ color: '#475569', fontSize: 12, marginLeft: 6 }}>({row.commission_pct}%)</span></div>
                <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 700 }}>{fmtMoney(row.doctor_amount)} ₽</div>
                <div style={{ ...styles.td, color: '#f59e0b' }}>{fmtMoney(row.platform_fee)} ₽</div>
                <div style={styles.td}>{row.status === 'paid' ? <span style={styles.badgeGreen}>Оплачено</span> : <span style={styles.badgeYellow}>Ожидает оплаты</span>}</div>
              </div>
            ))}
            {opItems.length === 0 && <div style={styles.empty}>Закрытых операционных направлений не найдено</div>}
          </div>
          <ListPager total={opTotal} limit={COM_LIMIT} offset={opOffset} onPage={setOpOffset} />
        </>
      )}

      {showPwd && (
        <div style={styles.modalOverlay} onClick={() => setShowPwd(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Сменить пароль врача</h3>
            <div style={styles.modalText}>
              Новый пароль для входа врача в кабинет (логин: <b style={{ color: '#e2e8f0' }}>{data.email}</b>).
              Полезно для демонстраций и техподдержки.
            </div>
            <div style={styles.fieldLabel}>Новый пароль</div>
            <input style={styles.modalInput} type="text" value={pwd} onChange={e => setPwd(e.target.value)} autoFocus placeholder="минимум 6 символов" />
            {pwdMsg && <div style={styles.errText}>{pwdMsg}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowPwd(false)}>Отмена</button>
              <button style={styles.btn} onClick={savePwd} disabled={busy}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {showClearBase && (
        <div style={styles.modalOverlay} onClick={() => setShowClearBase(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Сбросить счётчик и разблокировать врача?</h3>
            <p style={styles.modalText}>
              Обнулит счётчик недошедших направлений (база недошедших направлений) и разблокирует
              кабинет. Ничего не удаляется — активная база направлений не затрагивается, она
              сама рассасывается по мере визитов/сгораний.
            </p>
            {clearBaseMsg && <div style={styles.errText}>{clearBaseMsg}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowClearBase(false)}>Отмена</button>
              <button style={styles.btnDanger} onClick={resetNonArrived} disabled={busy}>Сбросить и разблокировать</button>
            </div>
          </div>
        </div>
      )}

      {showSuspend && (
        <div style={styles.modalOverlay} onClick={() => setShowSuspend(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Заблокировать кабинет врача?</h3>
            <p style={styles.modalText}>
              Врач больше не сможет добавлять пациентов и увидит в кабинете экран «Кабинет заблокирован»
              с указанной причиной. Вход в кабинет и просмотр истории остаются доступны.
              <br /><br />Для подтверждения введите точное ФИО врача:
              <br /><b style={{ color: '#e2e8f0' }}>{data.name}</b>
            </p>
            <input style={styles.modalInput} placeholder="ФИО врача" value={suspendConfirmName} onChange={e => setSuspendConfirmName(e.target.value)} autoFocus />
            <div style={{ ...styles.fieldLabel, marginTop: 6 }}>Причина (видна врачу)</div>
            <textarea style={styles.modalTextarea} rows={3} placeholder="Например: жалоба клиники на некорректные направления"
              value={suspendReason} onChange={e => setSuspendReason(e.target.value)} />
            {suspendMsg && <div style={styles.errText}>{suspendMsg}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => { setShowSuspend(false); setSuspendConfirmName(''); setSuspendReason(''); }}>Отмена</button>
              <button style={{ ...styles.btnDanger, ...(suspendConfirmName.trim() === data.name.trim() ? {} : styles.btnDisabled) }}
                onClick={doSuspend} disabled={busy || suspendConfirmName.trim() !== data.name.trim()}>Заблокировать</button>
            </div>
          </div>
        </div>
      )}

      {showDeactivate && (
        <div style={styles.modalOverlay} onClick={() => setShowDeactivate(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отключить аккаунт врача?</h3>
            <p style={styles.modalText}>
              Жёстче блокировки кабинета: врач больше не сможет ДАЖЕ ВОЙТИ в кабинет
              (для сценария «закрыл ИП» / «попросил удалить аккаунт»). В отличие от
              блокировки кабинета, врач не увидит причину — он просто не сможет залогиниться.
              <br /><br />Для подтверждения введите точное ФИО врача:
              <br /><b style={{ color: '#e2e8f0' }}>{data.name}</b>
            </p>
            <input style={styles.modalInput} placeholder="ФИО врача" value={deactivateConfirmName} onChange={e => setDeactivateConfirmName(e.target.value)} autoFocus />
            <div style={{ ...styles.fieldLabel, marginTop: 6 }}>Причина (для внутренней истории, врачу не показывается)</div>
            <textarea style={styles.modalTextarea} rows={3} placeholder="Например: врач попросил закрыть аккаунт"
              value={deactivateReason} onChange={e => setDeactivateReason(e.target.value)} />
            {deactivateMsg && <div style={styles.errText}>{deactivateMsg}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => { setShowDeactivate(false); setDeactivateConfirmName(''); setDeactivateReason(''); }}>Отмена</button>
              <button style={{ ...styles.btnDanger, ...(deactivateConfirmName.trim() === data.name.trim() ? {} : styles.btnDisabled) }}
                onClick={doDeactivate} disabled={busy || deactivateConfirmName.trim() !== data.name.trim()}>Отключить</button>
            </div>
          </div>
        </div>
      )}

      {showReject && (
        <div style={styles.modalOverlay} onClick={() => setShowReject(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отклонить верификацию?</h3>
            <p style={styles.modalText}>
              Врач увидит причину в кабинете и сможет загрузить документ повторно.
            </p>
            <div style={styles.fieldLabel}>Причина отказа</div>
            <textarea style={styles.modalTextarea} rows={3} placeholder="Например: документ нечитаем / не относится к врачу"
              value={rejectReason} onChange={e => setRejectReason(e.target.value)} autoFocus />
            {verifyMsg && <div style={styles.errText}>{verifyMsg}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowReject(false)}>Отмена</button>
              <button style={styles.btnDanger} onClick={rejectVerification} disabled={busy}>Отклонить</button>
            </div>
          </div>
        </div>
      )}
      {showPayout && (
        <div style={styles.modalOverlay} onClick={() => setShowPayout(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Реквизиты выплат</h3>
            <div style={styles.grid}>
              {PAYOUT_FIELDS.map(([field, label]) => (
                <Field key={field} label={label} value={data[field] || '—'} />
              ))}
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowPayout(false)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
      {showProfileReject && (
        <div style={styles.modalOverlay} onClick={() => setShowProfileReject(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отклонить заявку на изменение профиля?</h3>
            <p style={styles.modalText}>Врач увидит причину в кабинете и сможет отправить заявку заново.</p>
            <div style={styles.fieldLabel}>Причина отказа</div>
            <textarea style={styles.modalTextarea} rows={3} placeholder="Например: реквизиты не соответствуют налоговому статусу"
              value={profileRejectReason} onChange={e => setProfileRejectReason(e.target.value)} autoFocus />
            {profileMsg && <div style={styles.errText}>{profileMsg}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowProfileReject(false)}>Отмена</button>
              <button style={styles.btnDanger} onClick={() => rejectProfileRequest(data.pending_profile_request.id)} disabled={busy}>Отклонить</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// Единый статус-бейдж врача (вместо конфликтующих is_active/is_suspended рядом).
// Приоритет: ручная блокировка кабинета > анти-фрод блок > отключён > активен.
// Ортогонален VerificationBadge (верификация ЕГИСЗ) — показываются рядом, не сливаются в одну метку.
function StatusBadge({ data }) {
  if (data.is_suspended) return <span style={styles.badgeRed}>Кабинет заблокирован</span>;
  if (data.is_blocked) return <span style={styles.badgeRed}>Заблокирован (анти-фрод)</span>;
  if (!data.is_active) return <span style={styles.badgeRed}>Отключён</span>;
  return <span style={styles.badgeGreen}>Активен</span>;
}

// N-020: верификация по выписке ЕГИСЗ — отдельный бейдж, независимый от StatusBadge.
export function VerificationBadge({ status }) {
  if (status === 'active') return <span style={styles.badgeGreen}>Верифицирован</span>;
  if (status === 'pending') return <span style={styles.badgeYellow}>На проверке</span>;
  if (status === 'rejected') return <span style={styles.badgeRed}>Отклонён</span>;
  return <span style={styles.badgeGray}>Новый</span>;
}

function Field({ label, value }) { return (<div><div style={styles.fieldLabel}>{label}</div><div style={styles.fieldValue}>{value}</div></div>); }
function Metric({ label, value, color }) { return (<div style={styles.metric}><div style={styles.metricLabel}>{label}</div><div style={{ ...styles.metricValue, ...(color ? { color } : {}) }}>{value}</div></div>); }
function LimitRow({ label, used, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const color = used >= max ? '#f87171' : pct >= 80 ? '#f59e0b' : '#2EC4B6';
  return (
    <div style={styles.limitRow}>
      <span style={styles.limitLabel}>{label}</span>
      <div style={styles.limitTrack}><div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: color }} /></div>
      <span style={{ ...styles.limitVal, color }}>{used} / {max}</span>
    </div>
  );
}

const refCols = '0.8fr 2fr 1.5fr 1.3fr 1.2fr 1fr 1fr';
const comCols = '1.5fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1.2fr';
const opCols = '1.8fr 1.1fr 1.3fr 1.3fr 1fr 1fr 1.2fr';

const styles = {
  back: { color: '#64748b', fontSize: 13, cursor: 'pointer', marginBottom: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: '#64748b', fontSize: 14, marginTop: 4 },
  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #334155', marginBottom: 24 },
  tab: { padding: '10px 18px', color: '#94a3b8', fontSize: 14, fontWeight: 600, cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#2EC4B6', borderBottom: '2px solid #2EC4B6' },
  card: { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 },
  h2: { color: 'white', fontSize: 16, fontWeight: 600, margin: '0 0 12px' },
  h2row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  fieldLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  fieldValue: { fontSize: 14, color: '#e2e8f0' },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 },
  searchInput: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '7px 12px', fontSize: 13, outline: 'none', minWidth: 260 },
  select: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '7px 12px', fontSize: 13, outline: 'none', cursor: 'pointer' },
  dateInput: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none' },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 8 },
  limitsCard: { background: '#1e293b', borderRadius: 12, padding: 20, marginTop: 16 },
  limitsTitle: { fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 14 },
  limitRow: { display: 'grid', gridTemplateColumns: '220px 1fr 70px', alignItems: 'center', gap: 14, padding: '6px 0' },
  limitLabel: { fontSize: 13, color: '#94a3b8' },
  limitTrack: { height: 6, borderRadius: 3, background: '#0f172a', overflow: 'hidden' },
  limitVal: { fontSize: 14, fontWeight: 700, textAlign: 'right' },
  blockBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 14, padding: '12px 14px', background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 8, color: '#fca5a5', fontSize: 13 },
  unblockBtn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  warnNote: { marginTop: 14, padding: '10px 14px', background: '#422006', border: '1px solid #854d0e', borderRadius: 8, color: '#fcd34d', fontSize: 12 },
  metric: { background: '#1e293b', borderRadius: 12, padding: 20 },
  metricLabel: { fontSize: 13, color: '#64748b', marginBottom: 8 },
  metricValue: { fontSize: 26, fontWeight: 700, color: '#e2e8f0' },
  note: { color: '#64748b', fontSize: 12, marginBottom: 24 },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', alignItems: 'center' },
  link: { color: '#2EC4B6', cursor: 'pointer' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '32px 0' },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block', border: '1px solid #334155' },
  badgeRed: { background: '#450a0a', color: '#f87171', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeYellow: { background: '#422006', color: '#fbbf24', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  actionMsg: { color: '#2EC4B6', fontSize: 12 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#1e293b', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #334155' },
  modalTitle: { color: 'white', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' },
  modalInput: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginBottom: 8 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
  modalTextarea: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginBottom: 8, fontFamily: 'inherit', resize: 'vertical' },
  errText: { color: '#f87171', fontSize: 13, marginTop: 8 },
  btnDanger: { background: '#450a0a', color: '#f87171', border: '1px solid #7f1d1d', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { opacity: 0.4, cursor: 'default' },
  dangerZone: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },
};
